import React, { useState, useEffect } from 'react';
import { getAllBookings, updateBookingStatus } from '../api/admin';
import '../css/bookingManagement.css';

function BookingManagement() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState({}); // Track individual booking status updates
  const [refreshing, setRefreshing] = useState(false); // Track refresh state

  useEffect(() => {
    // Get current admin user from localStorage
    const auth = localStorage.getItem('adminAuth');
    if (auth) {
      try {
        const parsedAuth = JSON.parse(auth);
        setCurrentUser(parsedAuth);
      } catch (error) {
        console.error('Error parsing auth data:', error);
      }
    }

    fetchBookings();
  }, []);

  const fetchBookings = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        setError(''); // Clear any existing errors
      } else {
        setLoading(true);
      }
      
      const response = await getAllBookings();
      if (response.success) {
        setBookings(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchBookings(true);
  };

  const handleStatusUpdate = async (bookingId, status) => {
    try {
      if (!currentUser || !currentUser._id) {
        setError('User authentication required');
        return;
      }

      // Set loading state for this specific booking
      setLoadingStatus(prev => ({
        ...prev,
        [bookingId]: status === 'accepted' ? 'accepting' : status === 'rejected' ? 'rejecting' : 'cancelling'
      }));

      const response = await updateBookingStatus(bookingId, status, currentUser._id);
      
      if (response.success) {
        // Update the booking in the state
        setBookings(prevBookings =>
          prevBookings.map(booking =>
            booking._id === bookingId
              ? { ...booking, status, reviewedBy: currentUser, reviewedAt: new Date() }
              : booking
          )
        );
        
        // Clear error if it existed
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      // Clear loading state for this booking
      setLoadingStatus(prev => {
        const updated = { ...prev };
        delete updated[bookingId];
        return updated;
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      accepted: 'status-accepted',
      rejected: 'status-rejected',
      cancelled: 'status-cancelled'
    };

    return (
      <span className={`status-badge ${statusClasses[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="booking-management">
      <div className="management-header">
        <h2>Booking Management</h2>
        <p>Review and manage all booking requests</p>
      </div>
      <div className="bookings-list">
        {[1, 2, 3].map(index => (
          <div key={index} className="booking-card skeleton-card">
            <div className="skeleton-header">
              <div className="skeleton-id"></div>
              <div className="skeleton-status"></div>
            </div>
            <div className="skeleton-content">
              <div className="skeleton-line long"></div>
              <div className="skeleton-line medium"></div>
              <div className="skeleton-line short"></div>
            </div>
            <div className="skeleton-buttons">
              <div className="skeleton-button"></div>
              <div className="skeleton-button"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="booking-management">
      <div className="management-header">
        <div className="header-content">
          <div className="header-text">
            <h2>Booking Management</h2>
            <p>Review and manage all booking requests</p>
          </div>
          <button 
            className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh bookings"
          >
            {refreshing ? (
              <>
                <div className="refresh-spinner"></div>
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt"></i>
                <span>Refresh</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bookings-list">
        {bookings.length === 0 ? (
          <div className="no-bookings">No bookings found</div>
        ) : (
          bookings.map(booking => (
            <div key={booking._id} className="booking-card">
              <div className="booking-header">
                <div className="booking-id">#{booking.bookingId}</div>
                {getStatusBadge(booking.status)}
              </div>
              
              <div className="booking-details">
                <div className="detail-row">
                  <strong>Customer:</strong> {booking.name}
                </div>
                <div className="detail-row">
                  <strong>Email:</strong> {booking.email}
                </div>
                <div className="detail-row">
                  <strong>Phone:</strong> {booking.phone}
                </div>
                <div className="detail-row">
                  <strong>Date:</strong> {formatDate(booking.date)}
                </div>
                <div className="detail-row">
                  <strong>Boat:</strong> {booking.boat}
                </div>
                <div className="detail-row">
                  <strong>Passengers:</strong> {booking.passengers.length + 1} people
                </div>
              </div>

              {booking.passengers && booking.passengers.length > 0 && (
                <div className="passengers-section">
                  <strong>Passenger Details:</strong>
                  <ul>
                    {booking.passengers.map((passenger, index) => (
                      <li key={index}>
                        {passenger.name} ({passenger.age}, {passenger.gender}, {passenger.country})
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {booking.reviewedBy && (
                <div className="review-info">
                  Reviewed by: {booking.reviewedBy.firstName} {booking.reviewedBy.lastName} 
                  on {formatDate(booking.reviewedAt)}
                </div>
              )}

              {booking.status === 'pending' && (
                <div className="action-buttons">
                  <button 
                    className={`btn-accept ${loadingStatus[booking._id] === 'accepting' ? 'loading' : ''}`}
                    onClick={() => handleStatusUpdate(booking._id, 'accepted')}
                    disabled={loadingStatus[booking._id]}
                  >
                    {loadingStatus[booking._id] === 'accepting' ? (
                      <>
                        <div className="button-spinner"></div>
                        <span>Accepting...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i>
                        <span>Accept</span>
                      </>
                    )}
                  </button>
                  <button 
                    className={`btn-reject ${loadingStatus[booking._id] === 'rejecting' ? 'loading' : ''}`}
                    onClick={() => handleStatusUpdate(booking._id, 'rejected')}
                    disabled={loadingStatus[booking._id]}
                  >
                    {loadingStatus[booking._id] === 'rejecting' ? (
                      <>
                        <div className="button-spinner"></div>
                        <span>Rejecting...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times"></i>
                        <span>Reject</span>
                      </>
                    )}
                  </button>
                  <button 
                    className={`btn-cancel ${loadingStatus[booking._id] === 'cancelling' ? 'loading' : ''}`}
                    onClick={() => handleStatusUpdate(booking._id, 'cancelled')}
                    disabled={loadingStatus[booking._id]}
                  >
                    {loadingStatus[booking._id] === 'cancelling' ? (
                      <>
                        <div className="button-spinner"></div>
                        <span>Cancelling...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-ban"></i>
                        <span>Cancel</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default BookingManagement;